import { Component, Input } from '@angular/core';
import { ThreadNoteUserMention } from '@app/shared/services/thread/thread.service';

export enum ThreadNoteType {
  Internal = 'internal',
  AI = 'ai',
}

@Component({
  standalone: false,
  selector: 'sbnb-thread-note',
  templateUrl: './thread-note.component.html',
  styleUrls: ['./thread-note.component.scss'],
})
export class ThreadNoteComponent {
  @Input() type: ThreadNoteType;
  @Input() created_at_label: string;
  @Input() text: string;
  @Input() avatar: string;
  @Input() author: string;
  @Input() userMention: ThreadNoteUserMention | null;
  @Input() threadUuid: string;
  @Input() noteUuid: string;

  get cleanedHtml(): string {
    if (!this.text) return '';

    // Create a temporary DOM element to parse the HTML safely
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.text;

    // Sanitize HTML by preserving only user mentions and basic text elements
    return this.sanitizeHtml(tempDiv);
  }

  private sanitizeHtml(element: HTMLElement): string {
    let result = '';

    for (const node of Array.from(element.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        // Preserve text content
        result += node.textContent || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const elem = node as HTMLElement;
        const tagName = elem.tagName.toLowerCase();

        if (tagName === 'p') {
          // Preserve paragraph tags and recursively process their content
          result += `<p>${this.sanitizeHtml(elem)}</p>`;
        } else if (tagName === 'span' && elem.classList.contains('user-mention')) {
          // Only preserve spans with user-mention class and their allowed attributes
          const uid = elem.getAttribute('data-uid') || '';
          const firstName = elem.getAttribute('data-first-name') || '';
          const lastName = elem.getAttribute('data-last-name') || '';
          const textContent = elem.textContent || '';

          result += `<span class="user-mention font-bold text-gray-900" data-uid="${uid}" data-first-name="${firstName}" data-last-name="${lastName}">${textContent}</span>`;
        } else if (tagName === 'a') {
          // Preserve anchor tags with security attributes
          const href = elem.getAttribute('href') || '';
          const textContent = elem.textContent || '';

          // Only preserve links with valid URLs
          if (href && (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:'))) {
            result += `<a target="_blank" rel="noopener noreferrer nofollow" class="text-raspberry-darker underline" href="${href}">${textContent}</a>`;
          } else {
            // For invalid URLs, just extract text content
            result += textContent;
          }
        } else if (tagName === 'br') {
          // Preserve line breaks
          result += '<br>';
        } else {
          // For any other elements, just extract their text content
          result += elem.textContent || '';
        }
      }
    }

    // If no content was generated, wrap in a paragraph
    if (!result.trim()) {
      return '<p></p>';
    }

    // If content doesn't start with a block element, wrap it in a paragraph
    if (!result.trim().startsWith('<p>') && !result.trim().startsWith('<div>')) {
      return `<p>${result}</p>`;
    }

    return result;
  }

  get extractedText(): string {
    if (!this.text) return '';

    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.text;

    // Extract just the text content
    return tempDiv.textContent || tempDiv.innerText || '';
  }
}
